import { Box, Flex, Stack } from "@chakra-ui/react";
import { FC, useEffect, useRef } from "react";
import {
  Control,
  FieldValues,
  UseFormResetField,
  UseFormWatch,
} from "react-hook-form";
import InputFieldComponent from "../../../components/molecule/InputField/InputFieldComponent";
import { getProvince } from "../../../services/TabelasAuxiliares/getProvince";
import { getCountyById } from "../../../services/TabelasAuxiliares/getCountyById";
import { getRoleLevels } from "../../../services/getRoleLevels";
import FormSelect from "../../../components/molecule/Select/FormSelect";
import { useSkipFirstRender } from "../../../hooks/useSkipFirstRender";

interface OrganizationSubmitFormProps {
  errors: any;
  control: Control<any>;
  isDisabled?: boolean;
  resetField: UseFormResetField<FieldValues>;
  isFormInitialized?: boolean;
  watch: UseFormWatch<FieldValues>;
}

const OrganizationSubmitForm: FC<OrganizationSubmitFormProps> = ({
  errors,
  control,
  isDisabled,
  resetField,
  isFormInitialized = true,
  watch,
}) => {
  const selectedOrganizationLevel = isFormInitialized
    ? watch("organizationLevelOption")
    : undefined;
  const selectedProvince = isFormInitialized
    ? watch("provinceOption")
    : undefined;
  const prevSelectedProvince = useRef(selectedProvince);
  const skipFirstRender = useSkipFirstRender();
  useEffect(() => {
    if (!isFormInitialized) return;

    if (skipFirstRender()) {
      return;
    }

    if (selectedOrganizationLevel) {
      resetField("provinceOption");
      resetField("countyOption");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrganizationLevel]);

  useEffect(() => {
    if (prevSelectedProvince.current && !selectedProvince) {
      resetField("countyOption");
    }
    prevSelectedProvince.current = selectedProvince;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProvince]);

  return (
    <Stack gap={4}>
      <FormSelect
        control={control}
        errors={errors}
        watch={watch}
        fieldName="organizationLevelOption"
        fieldLabel="Tipo de entidade"
        fetchOptions={getRoleLevels}
        isDisabled={isDisabled}
      />
      <Flex gap={4} direction="row" justifyContent="flex-start">
        <Box flex={1}>
          <FormSelect
            control={control}
            errors={errors}
            watch={watch}
            fieldName="provinceOption"
            fieldLabel="Província"
            fetchOptions={getProvince}
            dependencyWatchField="organizationLevelOption"
            dependencyCondition={(selectedLevel) =>
              selectedLevel?.id >= 4 && selectedOrganizationLevel !== null
            }
            isRequiredCondition={(selectedLevel) =>
              selectedLevel?.id >= 4 && selectedOrganizationLevel !== null
            }
            isDisabled={
              isDisabled ||
              selectedOrganizationLevel === undefined ||
              selectedOrganizationLevel?.id <= 3
            }
          />
        </Box>

        <Box flex={1}>
          <FormSelect
            control={control}
            errors={errors}
            watch={watch}
            fieldName="countyOption"
            fieldLabel="Município"
            fetchOptions={() =>
              getCountyById({ ParentId: selectedProvince?.id })
            }
            dependencyWatchField="organizationLevelOption"
            dependencyCondition={(selectedProvince) => !!selectedProvince}
            isRequiredCondition={(selectedLevel) =>
              selectedLevel?.id === 5 && selectedOrganizationLevel !== null
            }
            isDisabled={
              isDisabled ||
              selectedOrganizationLevel === null ||
              !selectedProvince ||
              selectedOrganizationLevel?.id !== 5
            }
          />
        </Box>
      </Flex>
      <InputFieldComponent
        label="Nome"
        name="name"
        type="text"
        register={control.register}
        errors={errors}
        isRequired
        isDisabled={isDisabled}
      />
      <InputFieldComponent
        label="Email"
        name="email"
        type="text"
        register={control.register}
        errors={errors}
        isDisabled={isDisabled}
      />
    </Stack>
  );
};

export default OrganizationSubmitForm;
